import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, ShieldAlert, Loader2, Coins } from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getStoredEmail } from "../lib/useNewsezeUser";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// In-site iframe wrapper for the original publisher page. Keeps a Newseze
// nav frame around the external content so readers never leave the site.
// If the publisher sends X-Frame-Options: DENY (most majors do), we surface
// a "Open in new tab" fallback alongside our analysis + brief.
export default function ExternalRead() {
  const { id } = useParams();
  const nav = useNavigate();
  const [article, setArticle] = useState(null);
  const [iframeOk, setIframeOk] = useState(false);
  const [credited, setCredited] = useState(false);
  const startRef = useRef(Date.now());
  const email = getStoredEmail();

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/articles/${id}`).then((r) => alive && setArticle(r.data)).catch(() => {});
    return () => { alive = false; };
  }, [id]);

  // Award read credit after 25 seconds (anti-gaming gate)
  useEffect(() => {
    if (!email || !article || credited) return;
    const t = setTimeout(async () => {
      try {
        const r = await axios.post(`${API}/engagement`, {
          email, kind: "article", ref_id: id,
          duration_sec: Math.max(25, Math.round((Date.now() - startRef.current) / 1000)),
        });
        if (r.data?.awarded > 0) setCredited(true);
      } catch {}
    }, 25000);
    return () => clearTimeout(t);
  }, [email, article, credited, id]);

  const host = useMemo(() => {
    try { return article ? new URL(article.link).hostname.replace(/^www\./, "") : ""; }
    catch { return ""; }
  }, [article]);

  return (
    <>
      <Header onOpenBrief={() => {}} />
      {/* Newseze top frame — always visible while reading external content */}
      <div className="sticky top-0 z-20 bg-[var(--ink)] text-white border-b border-[var(--gold)]" data-testid="external-frame-bar">
        <div className="max-w-[1380px] mx-auto px-3 py-2 flex items-center gap-2 flex-wrap">
          <button onClick={() => nav(-1)} className="func-link !text-white !bg-transparent hover:!bg-white/10 !border-white/20" data-testid="external-back-btn">
            <ArrowLeft size={12} /> Back to Newseze
          </button>
          <Link to="/" className="font-black tracking-[-0.02em] text-[14px]">Newseze</Link>
          <span className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--gold)] hidden sm:inline">Global News Analysis</span>
          {article && (
            <span className="text-[11px] opacity-90 ml-auto truncate max-w-[420px] hidden md:inline">
              Reading <b>{article.source}</b> · {host}
            </span>
          )}
          {credited && (
            <span className="text-[10px] uppercase tracking-[0.14em] font-black bg-[var(--gold)] text-[#1d1303] rounded-full px-2 py-0.5 ml-auto md:ml-2 flex items-center gap-1" data-testid="external-credit-badge">
              <Coins size={10} /> +10
            </span>
          )}
        </div>
      </div>

      <main className="max-w-[1380px] mx-auto px-3 py-4" data-testid="external-read-page">
        {!article ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[var(--blue)]" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
            <div className="rounded-xl border border-[var(--line)] bg-white overflow-hidden" style={{ minHeight: "70vh" }}>
              {!iframeOk && (
                <div className="p-6 flex flex-col items-center justify-center text-center min-h-[60vh]" data-testid="external-fallback">
                  <ShieldAlert size={28} className="text-[var(--gold)] mb-3" />
                  <h3 className="text-[20px] font-black tracking-[-0.02em]">{article.source} prevents in-frame viewing</h3>
                  <p className="text-[13.5px] text-[var(--muted)] mt-2 max-w-[520px]">
                    Most major newsrooms set <code className="bg-[var(--bg)] px-1 rounded">X-Frame-Options: DENY</code>.
                    Read the publisher's page in a new tab — Newseze's analysis + brief stay right here.
                  </p>
                  <a href={article.link} target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer"
                     className="btn gold mt-4" data-testid="external-open-newtab">
                    <ExternalLink size={14} /> Open {article.source} in new tab
                  </a>
                  <Link to={`/article/${article.id}`} className="text-[12px] text-[var(--blue)] font-black mt-3 underline" data-testid="external-newseze-version">
                    Or read the Newseze Analysis →
                  </Link>
                </div>
              )}
              <iframe
                title={article.title}
                src={article.link}
                onLoad={() => setIframeOk(true)}
                className={`w-full ${iframeOk ? "" : "hidden"}`}
                style={{ minHeight: "75vh", border: 0 }}
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                data-testid="external-iframe"
              />
            </div>

            <aside className="space-y-3">
              <div className="rounded-xl bg-gradient-to-br from-[#0a1f3d] to-[#1e40af] text-white p-4">
                <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--gold)]">Newseze read</div>
                <h2 className="text-[18px] font-black leading-tight mt-1">{article.title}</h2>
                <p className="text-[12.5px] opacity-90 mt-2 leading-snug">{article.summary?.slice(0, 280)}</p>
                <div className="flex items-center gap-2 mt-3 text-[10px] uppercase tracking-[0.14em] font-black">
                  <span className="bg-white/10 rounded-full px-2 py-0.5">trust {article.trust}</span>
                  {/* lean label removed — Newseze does not categorize stories by political tilt in public UI */}
                </div>
                <Link to={`/article/${article.id}`} className="block mt-3 bg-[var(--gold)] text-[#1d1303] font-black text-center py-2 rounded-full text-[12px] uppercase tracking-[0.14em]" data-testid="external-go-newseze-article">
                  Read Newseze Analysis →
                </Link>
              </div>
            </aside>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
